import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Tag, MapPin, Map, Sparkles, UserRound } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { CL_REGIONS } from "@/lib/clRegions";

const ICON = { category: Tag, commune: MapPin, region: Map, workshop: Sparkles, maestro: UserRound };
const KIND = { category: "Categoría", commune: "Comuna", region: "Región", workshop: "Taller", maestro: "Maestro" };
const regionOf = (c) => Object.keys(CL_REGIONS).find((r) => CL_REGIONS[r].includes(c));

export function SearchSuggest({ q, setQ, onCategory, onPlace }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const box = useRef(null);
  useEffect(() => {
    const term = q.split(/\s+/).pop() || "";
    if (term.length < 2) { setItems([]); return; }
    const t = setTimeout(() => { api.get("/search/suggest", { params: { q: term } }).then((r) => setItems(r.data)).catch(() => {}); }, 200);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    const h = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const pick = (it) => {
    setOpen(false);
    const rest = q.split(/\s+/).slice(0, -1).join(" ");
    if (it.type === "workshop") { navigate(`/taller/${it.id}`); return; }
    if (it.type === "maestro") { navigate(`/maestro/${it.id}`); return; }
    if (it.type === "category") { onCategory(it.label); setQ(rest); return; }
    onPlace(it.type === "region" ? it.label : regionOf(it.label), it.type === "commune" ? it.label : "all");
    setQ(rest);
  };
  return (
    <div className="relative flex-1" ref={box}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
      <Input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
        onKeyDown={(e) => { if (e.key === "Escape" || e.key === "Enter") setOpen(false); }}
        data-testid="catalog-search" placeholder="Busca un taller, Maestro o lugar" className="pl-10 h-12 bg-background" />
      {open && items.length > 0 && (
        <div className="absolute z-30 left-0 right-0 mt-1 rounded-xl border bg-popover text-left shadow-lg overflow-y-auto max-h-80" data-testid="search-suggestions">
          {items.map((it, i) => {
            const Icon = ICON[it.type];
            return (
              <button key={`${it.type}-${it.label}-${i}`} type="button" onClick={() => pick(it)} data-testid={`search-suggestion-${i}`}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted transition-colors">
                <Icon className="w-4 h-4 text-primary shrink-0" />
                <span className="truncate flex-1 text-left">{it.label}</span>
                <span className="text-xs text-muted-foreground shrink-0">{it.detail ? `${KIND[it.type]} · ${it.detail}` : KIND[it.type]}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function SearchNotice({ info }) {
  if (!info) return null;
  const it = info.interpreted || {};
  const chips = [it.category, it.commune || it.region, it.kids && "Para niños", it.weekend && "Fin de semana",
    it.max_price && `Hasta $${Number(it.max_price).toLocaleString("es-CL")}`].filter(Boolean);
  if (!info.message && chips.length === 0) return null;
  return (
    <div className="mb-5 space-y-2" data-testid="search-notice">
      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 text-xs" data-testid="search-interpreted">
          <span className="text-muted-foreground">Buscando:</span>
          {chips.map((c) => <span key={c} className="rounded-full bg-primary/10 text-primary font-semibold px-2 py-0.5">{c}</span>)}
          {info.ai && <span className="inline-flex items-center gap-1 text-muted-foreground"><Sparkles className="w-3 h-3" />interpretado con IA</span>}
        </div>
      )}
      {info.message && <p className="rounded-xl border border-amber-300 bg-amber-50 text-amber-900 text-sm p-3" data-testid="search-fallback-message">{info.message}</p>}
    </div>
  );
}
